// Labels, hotkeys and plain-English wording for the dialer. Pure; unit-tested.
import type { DispositionCode, GateReasonCode, LineType, MembershipStatus, ObjectionCategory } from '../../../src/shared/api';
import { QUICK_CALLBACK_DISPOSITIONS } from '../../../src/shared/api';

export interface DispositionMeta {
  label: string;             // how a logged call reads (history, the Saved chip)
  button?: string;           // the outcome button's text when it differs (a merged button, REVIEW-2026-10-01)
  hint: string;              // tooltip: when to use it (SETTER-PLAYBOOK wording)
  key: string | null;        // hotkey shown on the button; null = not setter-selectable
  spokeToDm: boolean;        // default for the "spoke to DM" toggle
  needsCallback?: boolean;   // callbackAt required (SPEC §8.3)
  tone: 'neutral' | 'good' | 'bad';
}

/**
 * Every outcome code. `vm_dropped` is set only by the drop endpoint. REVIEW-2026-10-01: the bar shows 9 (DISPOSITION_ORDER);
 * "DM: not now" is merged into "Callback" and the two gatekeeper codes into one "Gatekeeper" button (wrap.ts picks intel or
 * blocked from what was typed). The old keys 4 and 6 still pick the merged buttons. Meeting booked is the Book panel (B).
 */
export const DISPOSITION_META: Record<DispositionCode, DispositionMeta> = {
  no_answer:            { label: 'No answer', hint: 'Rang out, busy, or a phone menu dead end.', key: '1', spokeToDm: false, tone: 'neutral' },
  vm_live:              { label: 'Left live VM', hint: 'You read a voicemail yourself.', key: '2', spokeToDm: false, tone: 'neutral' },
  gatekeeper_intel:     { label: 'Gatekeeper: intel', button: 'Gatekeeper', hint: "Someone else answered. Type the owner's name or a best time if they gave one (logged as intel); otherwise it's logged as a block.", key: '3', spokeToDm: false, tone: 'neutral' },
  gatekeeper_blocked:   { label: 'Gatekeeper: no', hint: 'Gatekeeper would not put you through or share anything.', key: '4', spokeToDm: false, tone: 'neutral' },
  dm_not_interested:    { label: 'DM: no interest', hint: 'Decision-maker said no. The business rests for 90 days in every campaign, then it can be called again. Not do-not-call.', key: '5', spokeToDm: true,  tone: 'neutral' },
  dm_later:             { label: 'DM: not now', hint: 'Decision-maker said not now; set a callback time.', key: '6', spokeToDm: true,  tone: 'neutral', needsCallback: true },
  dm_existing_solution: { label: 'DM: has one', hint: 'Decision-maker already has a provider or tool.', key: '7', spokeToDm: true,  tone: 'neutral' },
  callback_set:         { label: 'Callback set', button: 'Callback', hint: 'The decision-maker gave a time to call back (or said not now): set it below.', key: '8', spokeToDm: true,  tone: 'good', needsCallback: true },
  not_a_fit:            { label: 'Not a fit', hint: 'Chain/MSO, closed, or another disqualifier.', key: '9', spokeToDm: false, tone: 'neutral' },
  wrong_number:         { label: 'Wrong number', hint: 'Not this business; marks the number bad.', key: '0', spokeToDm: false, tone: 'bad' },
  dnc_request:          { label: 'Do not call', hint: 'They asked not to be called ("take me off your list"): never call them again, any campaign.', key: 'X', spokeToDm: false, tone: 'bad' },
  meeting_booked:       { label: 'Meeting booked', hint: 'Book a time, or link the booking they made on the Calendly link.', key: 'B', spokeToDm: true,  tone: 'good' },
  vm_dropped:           { label: 'Voicemail dropped', hint: 'Set automatically by Drop VM.', key: null, spokeToDm: false, tone: 'neutral' },
};

/** The 9 outcome buttons on the bar, in order (REVIEW-2026-10-01: was 12). */
export const DISPOSITION_ORDER: DispositionCode[] = [
  'no_answer', 'vm_live', 'gatekeeper_intel', 'dm_not_interested', 'dm_existing_solution', 'callback_set',
  'not_a_fit', 'wrong_number', 'dnc_request',
];

/** Old keys of the merged outcomes: 4 (gatekeeper: no) picks Gatekeeper, 6 (DM: not now) picks Callback. */
export const MERGED_OUTCOME_KEYS: Record<string, DispositionCode> = { '4': 'gatekeeper_intel', '6': 'callback_set' };

/** Hotkey (upper-cased) → disposition, for the buttons on the bar (a meeting is booked in the Book panel, B). */
export const DISPOSITION_BY_KEY: Record<string, DispositionCode> = {
  ...Object.fromEntries(DISPOSITION_ORDER.map((c) => [DISPOSITION_META[c].key!, c])),
  ...MERGED_OUTCOME_KEYS,
};

/** The text on an outcome button. */
export function buttonLabel(code: DispositionCode): string {
  return DISPOSITION_META[code].button ?? DISPOSITION_META[code].label;
}

/** The outcome button a code shows as: the merged codes light up the button they were merged into. */
export function outcomeButtonFor(code: DispositionCode | null): DispositionCode | null {
  if (code === 'gatekeeper_blocked') return 'gatekeeper_intel';
  if (code === 'dm_later') return 'callback_set';
  return code;
}

/** "Call back now" (owner request, PJ, 2026-10-06) is for a call that didn't get through or was cut off: these outcomes,
 *  or none picked yet (it then logs No answer). The rest close, rest or schedule the lead, so a redial right away would
 *  contradict them. The server's dial gate refuses the others too (shared QUICK_CALLBACK_DISPOSITIONS). */
export const CALL_BACK_NOW_OUTCOMES: readonly DispositionCode[] = QUICK_CALLBACK_DISPOSITIONS;

/** Why "Call back now" doesn't fit the outcome picked, in one sentence; null when it does. */
export function callBackOutcomeProblem(code: DispositionCode | null): string | null {
  if (!code || CALL_BACK_NOW_OUTCOMES.includes(code)) return null;
  return `Call back now is for a call that was hung up or cut off, not "${buttonLabel(outcomeButtonFor(code) ?? code)}". Pick No answer, Left live VM or Gatekeeper, or save with Enter.`;
}

/** Why a call whose outcome is already stored (a voicemail drop, a meeting, or what the save stored) can't be called
 *  back, in the server's words; null when it can. */
export function callBackLoggedProblem(code: DispositionCode): string | null {
  if (CALL_BACK_NOW_OUTCOMES.includes(code)) return null;
  return `They were logged as "${DISPOSITION_META[code].label}"; Call back now is only for hang-ups, voicemails and gatekeepers.`;
}

/** The note "Call back now" logs when no outcome was picked (PJ 2026-10-06). Written as it saves, before the redial is
 *  placed, so it says what is happening, not what happened. */
export const CALL_BACK_NOW_NOTE = 'Hung up / cut off; calling back now';

export const OBJECTION_LABELS: Record<ObjectionCategory, string> = {
  no_time: 'No time',
  not_interested: 'Not interested',
  send_info: 'Send me info',
  have_someone: 'Already have someone',
  too_expensive: 'Too expensive',
  bad_experience: 'Bad past experience',
  ai_skeptic: 'Skeptical of AI',
  need_partner: 'Need to ask partner',
  call_later: 'Call me later',
  other: 'Other',
};

export const MEMBERSHIP_STATUS_LABELS: Record<MembershipStatus, string> = {
  new: 'New',
  in_progress: 'In progress',
  callback: 'Callback',
  meeting: 'Meeting',
  closed_lost: 'Closed lost',
  disqualified: 'Not a fit',
  bad_data: 'Bad data',
  dnc: 'Do not call',
  exhausted: 'Out of attempts',
};

export function membershipTone(s: MembershipStatus): 'ok' | 'warn' | 'bad' | 'info' | '' {
  if (s === 'meeting') return 'ok';
  if (s === 'callback') return 'info';
  if (s === 'dnc' || s === 'bad_data') return 'bad';
  if (s === 'closed_lost' || s === 'disqualified' || s === 'exhausted') return 'warn';
  return '';
}

export const LINE_TYPE_LABELS: Record<LineType, string> = {
  landline: 'Landline',
  mobile: 'Mobile',
  fixedVoip: 'Fixed VoIP',
  nonFixedVoip: 'Non-fixed VoIP',
  personal: 'Personal',
  tollFree: 'Toll-free',
  premium: 'Premium',
  sharedCost: 'Shared cost',
  uan: 'UAN',
  voicemail: 'Voicemail line',
  pager: 'Pager',
  unknown: 'Unknown',
};

export function lineTypeLabel(t: LineType | null): string {
  return t ? LINE_TYPE_LABELS[t] ?? t : 'Line type unknown';
}

/** '+14165551234' → '(416) 555-1234'. Anything that is not a NANP E.164 number is returned unchanged. */
export function fmtPhone(e164: string | null | undefined): string {
  if (!e164) return '';
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

/** Normalises what a setter types as a North American mobile to E.164, or null when it isn't one. */
export function toE164Nanp(input: string): string | null {
  const digits = input.replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length !== 10) return null;
  if (/^[01]/.test(ten) || /^\d{3}[01]/.test(ten)) return null; // NPA and NXX cannot start with 0 or 1
  return '+1' + ten;
}

/** How each blocked-candidate count reads on the empty state. `{n}` = count, `{s}` = plural "s", `{are}` = is/are. */
const BLOCKED_WORDS: Record<GateReasonCode, string> = {
  outside_window: '{n} lead{s} {are} outside calling hours right now.',
  sunday: "{n} lead{s} {are} in a place where it's Sunday and Sunday calls aren't allowed there.",
  blocked_date: '{n} lead{s} {are} on a public holiday today.',
  attempt_spacing: '{n} lead{s} {were} called in the last 4 hours.',
  attempts_24h: '{n} lead{s} already had 2 calls in the last 24 hours.',
  attempts_day_limit: '{n} lead{s} already had 3 calls in the last 24 hours, the most we ever make to one number in a day.',
  attempts_week: '{n} lead{s} already had 3 calls in the last 7 days.',
  attempts_max: '{n} lead{s} used every attempt this campaign allows.',
  needs_dnc_scrub: '{n} lead{s} need{v} a Do Not Call scrub before calling (an admin runs it).',
  national_dnc: '{n} lead{s} {are} on the National Do Not Call Registry.',
  dnc_internal: '{n} lead{s} asked us not to call.',
  do_not_contact: '{n} lead{s} {are} marked do-not-contact.',
  bad_number: '{n} lead{s} {have} a bad number.',
  healthcare_ca: "{n} lead{s} {are} Canadian healthcare, which we can't cold call.",
  state_blocked: "{n} lead{s} {are} in a state or province we don't call.",
  state_unreviewed: "{n} lead{s} {are} in a state we haven't cleared yet.",
  state_line_type: '{n} lead{s} {are} in a state where we only call verified business landlines; {this} line type is mobile, unchecked or due for a re-check.',
  country_unsupported: '{n} lead{s} {are} outside the US and Canada.',
  not_certified: "{n} lead{s} can't be called until you pass certification for this campaign's niche (Training, once per niche).",
  not_assigned: "{n} lead{s} can't be called because you're not assigned to this campaign.",
  campaign_not_live: "{n} lead{s} can't be called because the campaign isn't live.",
  membership_closed: '{n} lead{s} {are} already closed.',
  locked_by_other: "{n} lead{s} {are} open on another setter's screen.",
  no_caller_number: '{n} lead{s} {have} no caller number free (daily limits reached); tell an admin.',
  not_interested_cooldown: '{n} lead{s} said not interested and {are} resting for 90 days.',
  drop_country: '', drop_line_type: '', drop_lookup_stale: '', drop_state: '', drop_toll_free: '', drop_vm_limit: '',
  drop_no_recording: '', drop_recording_unapproved: '', drop_not_answered: '',
};

export function blockedSentences(counts: Partial<Record<GateReasonCode, number>>): string[] {
  return (Object.entries(counts) as Array<[GateReasonCode, number]>)
    .filter(([code, n]) => n > 0 && BLOCKED_WORDS[code])
    .sort((a, b) => b[1] - a[1])
    .map(([code, n]) => {
      const one = n === 1;
      return BLOCKED_WORDS[code]
        .replace('{n}', String(n))
        .replace('{s}', one ? '' : 's')
        .replace('{are}', one ? 'is' : 'are')
        .replace('{were}', one ? 'was' : 'were')
        .replace('{have}', one ? 'has' : 'have')
        .replace('{this}', one ? 'its' : 'their')
        .replace('{v}', one ? 's' : '');
    });
}

// ---------- states and provinces ----------

const US_STATES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut',
  DE: 'Delaware', DC: 'Washington, DC', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
  VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  PR: 'Puerto Rico',
};
const CA_PROVINCES: Record<string, string> = {
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick', NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia', NT: 'Northwest Territories', NU: 'Nunavut', ON: 'Ontario', PE: 'Prince Edward Island',
  QC: 'Quebec', SK: 'Saskatchewan', YT: 'Yukon',
};

/** 'IL' → 'Illinois', 'ON' → 'Ontario'. Accepts 'US-IL' too. Unknown codes come back as given. */
export function stateName(country: 'US' | 'CA', code: string | null | undefined): string {
  const c = (code ?? '').trim().toUpperCase().replace(/^(US|CA)-/, '');
  return (country === 'CA' ? CA_PROVINCES[c] : US_STATES[c]) ?? (c || (country === 'CA' ? 'This province' : 'This state'));
}

/**
 * The state's script duty for this lead (QueueItem.stateNote, from StateRule.setterNote), as the one line pinned above
 * the script: "Illinois: Right away say your name, …". null when the state has none.
 */
export function stateNoteLine(business: { country: 'US' | 'CA'; stateProv: string }, note: string | null | undefined): { place: string; text: string } | null {
  const place = stateName(business.country, business.stateProv);
  let text = (note ?? '').trim();
  // A note that already starts with the place ("Oklahoma: pick each business…") would read "Oklahoma: Oklahoma: …".
  const prefix = `${place}:`;
  if (text.toLowerCase().startsWith(prefix.toLowerCase())) text = text.slice(prefix.length).trim();
  if (!text) return null;
  return { place, text };
}

// ---------- hand-pick-only states (migration 0006) ----------

/** One hand-pick-only state's count from queue/next (CORE `HandPickState`). */
export interface HandPickCount { code: string; name: string; count: number }

/** The words on the link to Leads, the three steps a setter takes. */
export const HAND_PICK_STEPS = 'Leads → filter by state → Call';

/**
 * The one line under the queue status when queue/next reports leads it will never serve (Oklahoma: "automated
 * selection OR dialing" law), e.g. "3 Oklahoma leads have to be opened by hand:". null when there are none.
 */
export function handPickLine(total: number | null | undefined, states: HandPickCount[] | null | undefined): string | null {
  const n = Number.isFinite(total) ? Math.max(0, Math.floor(total as number)) : 0;
  if (!n) return null;
  const names = (states ?? []).filter((x) => x && x.count > 0 && x.name).map((x) => x.name);
  const one = n === 1;
  const verb = one ? 'has' : 'have';
  if (names.length === 1) return `${n} ${names[0]} lead${one ? '' : 's'} ${verb} to be opened by hand:`;
  const where = names.length ? `in ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : 'in hand-pick-only states';
  return `${n} lead${one ? '' : 's'} ${where} ${verb} to be opened by hand:`;
}

/** Where the line links: Leads, filtered to this campaign and (when there is only one) to that state. */
export function handPickHref(campaignId: number | null | undefined, states: HandPickCount[] | null | undefined): string {
  const p = new URLSearchParams();
  if (campaignId) p.set('campaignId', String(campaignId));
  const withLeads = (states ?? []).filter((x) => x && x.count > 0 && x.code);
  if (withLeads.length === 1) p.set('state', withLeads[0].code);
  const qs = p.toString();
  return qs ? `/leads?${qs}` : '/leads';
}
